import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import { Spinner } from "../../components/ui/common";
import { Modal } from "../../components/ui/Modal";
import { MySelect } from "../../components/ui/Select";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import { Fine } from "./@types";
import { useCreateFineMutation, useUpdateFineMutation } from "./fineApiSlice";

export const fineFormSchema = z.object({
  name: z.string().nonempty("Name is required"),
  amount: z.number().nonnegative("Amount is required"),
  category: z.string().nonempty("Category is required"),
});

export type FineFormData = z.infer<typeof fineFormSchema>;

interface FineModelProps {
  isOpen: boolean;
  onClose: () => void;
  fine?: Fine | null;
  mode?: "add" | "edit";
}

const FineModal: React.FC<FineModelProps> = ({
  isOpen,
  onClose,
  fine = null,
  mode = fine ? "edit" : "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const toast = useToast();
  const isEditMode = mode === "edit" && fine;

  const [
    createFine,
    {
      isLoading: isCreating,
      isSuccess: createSuccess,
      isError: createError,
      error: createErrorData,
      reset: resetCreate,
    },
  ] = useCreateFineMutation();

  const [
    updateFine,
    {
      isLoading: isUpdating,
      isSuccess: updateSuccess,
      isError: updateError,
      error: updateErrorData,
      reset: resetUpdate,
    },
  ] = useUpdateFineMutation();

  // Derived states for cleaner code
  const isLoading = isCreating || isUpdating;
  const isSuccess = createSuccess || updateSuccess;
  const isError = createError || updateError;
  const error = createErrorData || updateErrorData;

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    reset,
  } = useForm<FineFormData>({
    resolver: zodResolver(fineFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      amount: 0,
      category: "",
    },
  });

  // Populate form with fine data when editing
  useEffect(() => {
    if (isEditMode && fine) {
      reset({
        name: fine.name,
        amount: fine.amount,
        category: fine.category,
      });
    } else if (!isEditMode) {
      // Reset to empty when switching to add mode
      reset({
        name: "",
        amount: 0,
        category: "",
      });
    }
  }, [isEditMode, fine, reset]);

  const onSubmit = async (data: FineFormData) => {
    if (isSubmitting || isLoading) return;

    // Prevent submission if editing and no changes were made
    if (isEditMode && !isDirty) {
      toast.warning({
        title: "No Changes",
        message: "No changes were made to update.",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: data.name,
        amount: data.amount,
        category: data.category,
      };

      if (isEditMode && fine?.id) {
        await updateFine({
          id: fine.id,
          ...payload,
        }).unwrap();
      } else {
        await createFine(payload).unwrap();
      }

      setShowSuccess(true);
    } catch (err) {
      console.error(`${isEditMode ? "Update" : "Create"} error:`, err);
      const errorMessage =
        getErrorDetails(err).message ||
        `Failed to ${isEditMode ? "update" : "add"} fine. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (isSuccess && !showSuccess) {
      toast.success({
        title: "Success",
        message: `Fine ${isEditMode ? "updated" : "added"} successfully`,
      });
      setShowSuccess(true);
    }

    if (isError) {
      const errorMessage =
        getErrorDetails(error).message ||
        `Failed to ${isEditMode ? "update" : "add"} fine. Please try again.`;

      toast.error({
        title: "Error",
        message: errorMessage,
      });
    }
  }, [isSuccess, isError, error, toast, isEditMode, showSuccess]);

  const handleCloseModal = () => {
    setShowSuccess(false);
    reset();
    resetCreate();
    resetUpdate();
    onClose();
  };

  const renderSuccessView = () => {
    if (showSuccess) {
      return (
        <SuccessView
          handleCloseModal={handleCloseModal}
          heading=""
          message={`Fine has been ${
            isEditMode ? "updated" : "added"
          } successfully.`}
        />
      );
    }
    return null;
  };

  const modalTitle = showSuccess ? "" : `${isEditMode ? "Edit" : "Add"} fine`;
  const submitButtonText = isEditMode ? "Update fine" : "+ Add fine";

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={modalTitle}
      className="max-w-full sm:max-w-[80vw] lg:max-w-[45vw] w-full bg-white"
    >
      {isSuccess ? (
        renderSuccessView()
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
          <div className="">
            <Controller
              control={control}
              name="name"
              render={({ field }) => (
                <AppInputField
                  label="Enter name"
                  type="text"
                  error={errors.name?.message}
                  {...field}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter name"
                />
              )}
            />
          </div>

          <div className="">
            <Controller
              control={control}
              name="amount"
              render={({ field }) => (
                <AppInputField
                  label="Enter amount"
                  type="number"
                  error={errors.amount?.message}
                  {...field}
                  onChange={(e) => field.onChange(e.target.valueAsNumber)}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                  placeholder="Enter amount"
                />
              )}
            />
          </div>

          <div>
            <label className="font-semibold text-[13px]">Enter category</label>
            <Controller
              control={control}
              name="category"
              render={({ field }) => (
                <MySelect
                  options={[
                    "Compliance And Regulaory",
                    "Safety And Driving Behavior",
                    "Cargo and Delivery Related Fines",
                    "Company Policy Violations",
                    "Vehicle Conditions and Maintenance",
                  ]}
                  onChange={field.onChange}
                  value={field.value}
                  name={field.name}
                  placeholder="Select category"
                  getName={(option) => option}
                  getValue={(option) => option}
                  className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
                />
              )}
            />
            {errors.category && (
              <p className="text-primary text-sm mt-1">
                {errors.category.message}
              </p>
            )}
          </div>

          <div className="flex justify-center items-center space-x-4 text-[15px]">
            <AppButton
              type="button"
              onClick={handleCloseModal}
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-[#F6F6F6] rounded-md text-hColor hover:bg-gray-50 transition-colors text-[15px] disabled:opacity-50"
            >
              Cancel
            </AppButton>

            <AppButton
              type="submit"
              disabled={isSubmitting || isLoading}
              className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/75 transition-colors disabled:opacity-50 text-[15px]"
            >
              {isSubmitting || isLoading ? (
                <>
                  <Spinner /> Adding
                </>
              ) : (
                submitButtonText
              )}
            </AppButton>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default FineModal;
